import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { BooksIcon } from '@phosphor-icons/react';
import {
  serializeProcSavedReport,
  type WorkbookDefinition,
  type WorkbookReportOutput,
} from '@erp/shared-types';
import { outputSheetIds } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { useCreateProcSavedReport } from '@/api/rpc/hooks/procurement/useProcSavedReports';
import { libraryReportLayout } from './workbookLibraryReport';

/**
 * Save to Library: saves one Worksheet's part of a Report Output as a library
 * report, which charts that Worksheet's newest Current Result wherever the
 * library is opened.
 */
export function WorkbookReportLibraryButton({
  workbookId,
  definition,
  output,
}: {
  workbookId: string;
  definition: WorkbookDefinition;
  output: WorkbookReportOutput;
}) {
  const gt = useGT();
  const navigate = useNavigate();
  const create = useCreateProcSavedReport();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [sheetId, setSheetId] = useState('');
  const [charted] = outputSheetIds(definition, output);
  const chosen =
    definition.sheets.find((sheet) => sheet.id === sheetId) ??
    definition.sheets.find((sheet) => sheet.id === charted) ??
    definition.sheets[0];
  function save() {
    if (!chosen || !name.trim()) return;
    create.mutate(
      {
        name: name.trim(),
        report: serializeProcSavedReport({
          source: { kind: 'workbook', workbookId, sheetId: chosen.id },
          report: libraryReportLayout(output.layout, definition, chosen.id),
        }),
      },
      {
        onSuccess: (saved) => {
          setOpen(false);
          void navigate({
            to: '/workbooks/reports/$reportId',
            params: { reportId: saved.id },
          });
        },
        onError: () =>
          toast.error(
            gt('Could not save the report.', {
              context: 'workbook report save to library failed',
            }),
          ),
      },
    );
  }
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1 text-xs"
        disabled={!definition.sheets.length}
        onClick={() => {
          setName(output.name);
          setSheetId(chosen?.id ?? '');
          setOpen(true);
        }}
      >
        <BooksIcon className="h-4 w-4" />
        <T context="workbook report save to library">Save to library</T>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <T context="workbook report save to library">Save to library</T>
            </DialogTitle>
            <DialogDescription>
              <T context="workbook report save to library description">
                Saves this worksheet's charts as a report in your report
                library. It charts the worksheet's latest run.
              </T>
            </DialogDescription>
          </DialogHeader>
          <form
            id="workbook-report-library"
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              save();
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="workbook-report-library-name">
                <T context="workbook library report name">Name</T>
              </Label>
              <Input
                id="workbook-report-library-name"
                value={name}
                maxLength={255}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>
                <T context="workbook library report worksheet">Worksheet</T>
              </Label>
              <Select value={chosen?.id ?? ''} onValueChange={setSheetId}>
                <SelectTrigger
                  aria-label={gt('Worksheet', {
                    context: 'workbook library report worksheet',
                  })}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {definition.sheets.map((sheet) => (
                    <SelectItem key={sheet.id} value={sheet.id}>
                      {sheet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </form>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              <T context="workbook library report cancel">Cancel</T>
            </Button>
            <Button
              type="submit"
              form="workbook-report-library"
              disabled={!name.trim() || !chosen || create.isPending}
            >
              <BooksIcon className="h-4 w-4" />
              <T context="workbook library report save">Save report</T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
